<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import AgreementVersionHistory from './AgreementVersionHistory.vue'
import { formatDayMonthYear } from '@shared/utils'
import { agreementTypeLabelOf } from '../../constants/listingRequestLifecycle'
import { groupAgreementsByVersion } from '../../utils/agreementVersions'
import { downloadDocument, openDocument } from '../../utils/agreementDocument'
import type { PropertyAgreement } from '../../types/listingRequestTypes'

// The executed agreements, read-only — the property's Documents tab lists them
// here rather than sending the seller back to the sell request. Only the latest
// version of each shows; the ones it replaced fold away under it.
//
// Signing never happens here: an agreement still out for signature lives on the
// sell request, where the seller can act on it.
const props = defineProps<{
  agreements: PropertyAgreement[]
  isLoading: boolean
  hasError: boolean
}>()

// Fully executed — every party, Sliceal included, has signed.
const EXECUTED_STATUSES: ReadonlySet<string> = new Set(['signed', 'approved'])

const signed = computed(() =>
  groupAgreementsByVersion(props.agreements).filter(
    ({ current }) => EXECUTED_STATUSES.has(current.status) && current.signedFilePath,
  ),
)

function captionOf(agreement: PropertyAgreement): string {
  const signedOn = agreement.signedAt ? ` · Signed ${formatDayMonthYear(agreement.signedAt)}` : ''
  return `Version ${agreement.version}${signedOn}`
}

function download(agreement: PropertyAgreement) {
  void downloadDocument(
    agreement.signedFilePath,
    `${agreementTypeLabelOf(agreement.agreementType)}.pdf`,
  )
}
</script>

<template>
  <Skeleton v-if="isLoading" height="72px" border-radius="12px" />

  <Message v-else-if="hasError" color="danger" variant="outlined">
    We couldn’t load your agreements. Refresh the page to try again.
  </Message>

  <Text v-else-if="!signed.length" type="caption" color="muted">
    Your signed agreements appear here once every party has signed.
  </Text>

  <Box v-else is="ul" direction="col" gap="3" class="signed">
    <Box
      v-for="{ current, history } in signed"
      :key="current.id"
      is="li"
      direction="col"
      gap="2"
      class="signed__row"
    >
      <Box flex align="center" gap="3">
        <PiIcon name="file-check" class="signed__icon" />
        <Box direction="col" gap="1" grow class="signed__body">
          <Text type="body" weight="medium" truncate>
            {{ agreementTypeLabelOf(current.agreementType) }}
          </Text>
          <Text type="caption-sm" color="muted">{{ captionOf(current) }}</Text>
        </Box>
        <Box flex align="center" gap="1" class="signed__actions">
          <Button
            icon="eye"
            variant="text"
            class="signed__action"
            :aria-label="`View ${agreementTypeLabelOf(current.agreementType)}`"
            @click="openDocument(current.signedFilePath)"
          />
          <Button
            icon="download"
            variant="text"
            class="signed__action"
            :aria-label="`Download ${agreementTypeLabelOf(current.agreementType)}`"
            @click="download(current)"
          />
        </Box>
      </Box>

      <AgreementVersionHistory v-if="history.length" :history="history" />
    </Box>
  </Box>
</template>

<style scoped>
.signed {
  margin: 0;
  padding: 0;
  list-style: none;
}

.signed__row {
  padding: 0.875rem 1rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  background: var(--p-surface-0);
}

.signed__icon {
  flex-shrink: 0;
  font-size: 1rem;
  color: var(--p-text-muted-color);
}

.signed__body {
  min-width: 0;
}

.signed__actions {
  flex-shrink: 0;
}

/* Icon-only, so the hit area comes from here rather than from a label. */
.signed__action {
  min-width: 2.5rem;
  min-height: 2.5rem;
}
</style>
